<template>
    <v-container fluid>
        <div class="d-flex">
            <v-tabs v-if="microData.length > 1" grow class="pb-4 ml-n4">
                <v-tab
                    v-for="(plate, index) in microData"
                    :key="index"
                    @click="setActivePlate(index)"
                >
                    <div>
                        <span>{{ fetchPlateName(plate, index) }}</span>
                    </div>
                </v-tab>
            </v-tabs>
        </div>
        <div v-for="h in columns" :key="`head${h}`" class="colLabel pl-12">
            {{ h }}
        </div>
        <div v-for="r in rows" :key="`row${r}`" class="micro-table-row">
            <div v-for="c in columns" :key="`col${c}`" style="display: inline-block">
                <div v-if="c === 1" class="rowLabel">
                    {{ String.fromCharCode(65 + r - 1) }}
                </div>
                <div
                    class="cell center-text"
                    v-bind:style="{
                        'background-color': fetchCell(r, c).rgba!,
                        'border': fetchCell(r, c).border,
                        'border-width': fetchCell(r, c).borderWidth,
                        'border-color': fetchBorderColor(fetchCell(r, c)),
                    }"
                >
                    <div
                        v-if="fetchCell(r, c).condition_type !== 'sample'"
                        class="smallText"
                        style="margin-top: 6px"
                    >
                        {{ fetchCell(r, c).condition_type }} <br />
                        {{ getCellSource(fetchCell(r, c)) }} <br />
                        {{ getCellConcentrationOrDilution(fetchCell(r, c)) }}
                        <br />
                    </div>
                    <div
                        v-else
                        v-for="(active, i) in fetchCell(r, c).batch"
                        :key="`act${i}`"
                        style="padding: 1px"
                        :class="fetchCell(r, c)?.batch!.length > 1 ? 'smallText' : 'largeText'"
                    >
                        {{ active }} <br />
                        {{
                            getSampleConditionTypeCellContents(fetchCell(r, c), i)
                        }}
                        <br />
                    </div>
                </div>
            </div>
        </div>
    </v-container>
</template>

<script lang="ts" setup>
import _ from "lodash";
import { ref, computed } from "vue";

export type MicroData = {
    condition_number?: number;
    strain_name?: string | undefined;
}

export type Cell = {
    rgba: string | null;
    border: string;
    borderWidth: string;
    borderColor?: string;
    batch?: string[] | undefined;
    source?: string | undefined;
    conc?: string[] | undefined;
    concUnits?: string[] | undefined;
    dilution_factor?: string | undefined;
    condition_type?: string | undefined;
}

const {
    microData,
    isMicrobial,
    columns = 12,
    rows = 8
} = defineProps<{
    microData: MicroData[][];
    isMicrobial: boolean;
    columns?: number;
    rows?: number;
}>();

const activePlate = ref(0);

const microTableData = computed(() => {
    return generateMicroTable(microData[activePlate.value]);
});

const colors1 = [
    "rgba(183, 96, 40",
    "rgba(191, 143, 0",
    "rgba(166, 166, 166",
    "rgba(94, 129, 63",
    "rgba(67, 116, 176",
    "rgba(154, 81, 155",
];
const colors2 = [
    "rgba(67, 116, 176",
    "rgba(154, 81, 155",
    "rgba(183, 96, 40",
    "rgba(191, 143, 0",
    "rgba(166, 166, 166",
    "rgba(94, 129, 63",
];
const colors3 = [
    "rgba(191, 143, 0",
    "rgba(166, 166, 166",
    "rgba(94, 129, 63",
    "rgba(67, 116, 176",
    "rgba(154, 81, 155",
    "rgba(183, 96, 40",
];

const opacities = [", 0.9)", ", 0.75)", ", 0.6)", ", 0.5)"];

function generateMicroTable(data: MicroData[]) {
    const cells: Cell[] = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < columns; c++) {
            const topBorder = r === 0 || (r === 4 && isMicrobial) ? "2px" : "0";
            const bottomBorder = isMicrobial
                ? r === rows - 1
                    ? "2px"
                    : "0"
                : "1px";
            const leftBorder = c === 0 ? "2px" : "1px";
            const rightBorder = c === columns - 1 ? "2px" : "1px";
            cells.push({
                rgba: isMicrobial
                    ? r < 4
                        ? colors1[c % 6] + opacities[r % 4]
                        : colors2[c % 6] + opacities[r % 4]
                    : null,
                border: "solid black",
                borderWidth: `${topBorder} ${rightBorder} ${bottomBorder} ${leftBorder}`,
            });
        }
    }

    for (let cell = 0; cell < columns * rows; cell++) {
        // TODO: refactor this part
        cells[cell].batch = _.get(data, `${cell}.batch`);
        cells[cell].source = _.get(data, `${cell}.source`);
        cells[cell].conc = _.get(data, `${cell}.concentration`);
        cells[cell].concUnits = _.get(data, `${cell}.concentration_units`);
        cells[cell].dilution_factor = _.get(data, `${cell}.dilution_factor`);
        cells[cell].condition_type = _.get(data, `${cell}.condition_type`);

        // Set color by condition if not Microbial
        if (!isMicrobial)
            cells[cell].rgba = _.get(data, `${cell}.condition_number`)
                ? colors3[data[cell]!.condition_number! % 6] + opacities[2]
                : null;
        // Style control wells for Microbial
        if (isMicrobial) {
            switch (cells[cell].condition_type) {
                case "media control": // blue
                    cells[cell].borderColor = "rgba(0, 150, 255)";
                    cells[cell].rgba = "rgba(255, 255, 255, 1)";
                    cells[cell].borderWidth = "3px";
                    break;
                case "positive control": // red
                case "positive control 1":
                    cells[cell].borderColor = "rgba(211, 45, 23)";
                    cells[cell].rgba = "rgba(255, 255, 255, 1)";
                    cells[cell].borderWidth = "3px";
                    break;
                case "negative control": // green
                    cells[cell].borderColor = "rgba(50, 205, 50)";
                    cells[cell].rgba = "rgba(255, 255, 255, 1)";
                    cells[cell].borderWidth = "3px";
                    break;
                case "positive control 2": // orange
                    cells[cell].borderColor = "rgba(255, 192, 0)";
                    cells[cell].rgba = "rgba(255, 255, 255, 1)";
                    cells[cell].borderWidth = "3px";
                    break;
            }
        }
    }

    return cells;
}

function setActivePlate(index: number) {
    activePlate.value = index;
}

function fetchCell(row: number, col: number) {
    return microTableData.value[(row - 1) * columns + (col - 1)];
}

function fetchBorderColor(cell: Cell): string {
    return cell.borderColor || "";
}

function fetchPlateName(plate: MicroData[], index: number) {
    const strain = _.find(plate, (well) => well.strain_name);
    return _.get(strain, "strain_name") || `Plate ${index + 1}`;
}

function getCellConcentrationOrDilution(cell: Cell) {
    if (cell.conc && cell.concUnits && cell.conc.length && cell.concUnits.length) {
        return `${cell.conc[0]} ${cell.concUnits[0]}`;
    }
    return cell.dilution_factor;
}

function getCellSource(cell: Cell) {
    if (cell.batch && cell.batch.length) {
        return cell.batch[0];
    }
    return cell.source;
}

function getSampleConditionTypeCellContents(cell: Cell, i: number): string {
    const first = (cell.conc && cell.conc[i]) ? cell?.conc[i] : cell.dilution_factor;
    const second = cell.concUnits && cell.concUnits[i] ? cell?.concUnits[i] : cell?.dilution_factor;

    return `${first} ${second}`;
}

defineExpose({
    setActivePlate
});
</script>

<style>
.micro-table-row {
    height: var(--cellHeight);
}
.colLabel {
    height: 30px;
    width: var(--cellWidth);
    display: inline-block;
    font-weight: 900;
    text-align: center;
}
.rowLabel {
    height: var(--cellHeight);
    width: 25px;
    float: left;
    text-align: left;
    padding: 24px 0px;
    font-weight: 900;
}
.cell {
    height: var(--cellHeight);
    width: var(--cellWidth);
    float: right;
    padding-top: 6px;
    overflow-y: hidden;
    overflow-x: hidden;
    text-align: center;
    font-size: 0.875rem;
    line-height: normal;
}
.smallText {
    font-size: 11px;
}
.largeText {
    margin-top: 6px;
    font-size: 14px;
}
</style>
